<app-navbar />

<main id="main-content">
  <section class="page-hero">
    <div class="container">
      <app-breadcrumb [trail]="[{ label: 'Pricing' }]" />
      <span class="eyebrow">Pricing</span>
      <h1>Simple usage based pricing. Pay only for verified records.</h1>
      <p>
        No contracts, no minimums and no setup fees. Standard verification is four cents per record.
        Verify at scale and your rate drops to one cent per record.
      </p>
      <p class="hero-price-note">
        Creating an account is free and needs no credit card. You are charged only when a record is
        confirmed as deceased.
      </p>
    </div>
  </section>

  <section class="section">
    <div class="container">
      <div class="plan-grid">
        <div class="plan">
          <span class="plan-name">Standard</span>
          <div class="plan-price">${{ pricing.standardPerRecord.toFixed(2) }}<small>/record</small></div>
          <p class="plan-desc">Pay as you go for any volume. Perfect for teams getting started.</p>
          <ul class="check-list">
            <li>No monthly minimum</li>
            <li>Batch file upload</li>
            <li>Real time API access</li>
            <li>Pay only for matches</li>
          </ul>
          <a class="btn btn-outline btn-block" [routerLink]="[routes.accountRegister.url]">Create free account</a>
        </div>

        <div class="plan featured">
          <span class="plan-flag">Best value</span>
          <span class="plan-name">Bulk</span>
          <div class="plan-price">${{ pricing.bulkPerRecord.toFixed(2) }}<small>/record</small></div>
          <p class="plan-desc">For high volume verification at 100,000 or more records per month.</p>
          <ul class="check-list">
            <li>Everything in Standard</li>
            <li>75% lower per record rate</li>
            <li>Priority support</li>
            <li>Volume reporting</li>
          </ul>
          <a class="btn btn-primary btn-block" [routerLink]="[routes.accountRegister.url]">Get started</a>
        </div>

        <div class="plan">
          <span class="plan-name">Enterprise</span>
          <div class="plan-price">Custom</div>
          <p class="plan-desc">For banks and platforms that need dedicated support and terms.</p>
          <ul class="check-list">
            <li>Custom volume pricing</li>
            <li>Dedicated account team</li>
            <li>SLA and security review</li>
            <li>API integration support</li>
          </ul>
          <a class="btn btn-outline btn-block" [routerLink]="[routes.contact.url]">Contact sales</a>
        </div>
      </div>
    </div>
  </section>

  <section class="section section-alt">
    <div class="container">
      <div class="section-head">
        <span class="eyebrow">Estimate your cost</span>
        <h2 class="section-title">See what verification would cost you</h2>
        <p class="section-lead">Drag to set your monthly volume. Bulk pricing kicks in automatically at 100,000 records.</p>
      </div>

      <div class="calculator">
        <div class="calc-top">
          <div>
            <span class="calc-label">Records per month</span>
            <span class="calc-volume">{{ volume().toLocaleString() }}</span>
          </div>
          <div class="calc-result">
            <span class="calc-label">Estimated monthly cost</span>
            <span class="calc-cost">${{ monthlyCost().toLocaleString() }}</span>
            <span class="calc-rate">at ${{ rate().toFixed(2) }} per record @if (isBulk()) {<span class="badge-success">Bulk rate</span>}</span>
          </div>
        </div>
        <input type="range" min="1000" max="1000000" step="1000" [value]="volume()"
          (input)="setVolume($any($event.target).value)" class="calc-slider"
          aria-label="Records per month" />
        <div class="calc-scale">
          <span>1K</span>
          <span>100K</span>
          <span>500K</span>
          <span>1M</span>
        </div>
      </div>

      <div class="card" style="max-width: 720px; margin: 32px auto 0;">
        <table class="data-table">
          <tbody>
            <tr class="table-head">
              <td>Records per month</td>
              <td>Rate</td>
              <td>Monthly cost</td>
            </tr>
            @for (row of examples; track row.volume) {
              <tr>
                <td>{{ row.volume }}</td>
                <td class="muted">{{ row.rate }}</td>
                <td>{{ row.cost }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </div>
  </section>

  <section class="section">
    <div class="container">
      <div class="section-head">
        <span class="eyebrow">Included with every plan</span>
        <h2 class="section-title">One rate. Everything included.</h2>
      </div>
      <ul class="check-list included-grid">
        @for (item of included; track item) {
          <li>{{ item }}</li>
        }
      </ul>
    </div>
  </section>

  <section class="section section-alt">
    <div class="container">
      <div class="section-head">
        <span class="eyebrow">Pricing questions</span>
        <h2 class="section-title">Frequently asked questions</h2>
      </div>
      <div class="faq-list">
        @for (faq of faqs; track faq.question) {
          <details class="faq-item">
            <summary>{{ faq.question }}</summary>
            <p>{{ faq.answer }}</p>
          </details>
        }
      </div>
    </div>
  </section>

  <section class="section">
    <div class="container">
      <div class="cta-band">
        <h2>Start verifying for four cents a record</h2>
        <p>
          Creating an account is free and needs no credit card. No contract, no minimum, and you pay
          only for the records we confirm as deceased.
        </p>
        <div class="actions-row center">
          <a class="btn btn-primary btn-lg" [routerLink]="[routes.accountRegister.url]">Create your free account</a>
          <a class="btn btn-outline btn-lg" [routerLink]="[routes.contact.url]">Talk to sales</a>
        </div>
      </div>
    </div>
  </section>
</main>

<app-footer />
